import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, ExternalLink } from "lucide-react";
import api, { apiError, fileUrl } from "@/lib/api";
import {
  formatBytes, formatDate, formatDateTime, formatNumber, metricCoverage, METRIC_KEYS,
  METRIC_LABELS, PLATFORM_METRIC_NOTE,
} from "@/lib/format";
import { PageHeader } from "@/components/portal/PortalLayout";
import { ReportDownload } from "@/components/portal/ReportDownload";
import { Badge, Card, Empty, ErrorNote, Loading, Note, ProgressBar } from "@/components/portal/Ui";

export function MetricTotals({ totals, testIdPrefix = "total" }) {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
      {METRIC_KEYS.map((key) => {
        const metric = totals[key];
        return (
          <div
            key={key}
            className="rounded-xl border border-[#EFEAE1] bg-white p-4"
            data-testid={`${testIdPrefix}-${key}`}
          >
            <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#8a8378]">
              {METRIC_LABELS[key]}
            </p>
            <p className="mt-1.5 text-xl font-extrabold tabular">
              {metric?.value == null ? (
                <span className="text-base font-semibold body-text">Not available</span>
              ) : (
                formatNumber(metric.value)
              )}
            </p>
            <p className="mt-1 text-[11px] leading-snug body-text">{metricCoverage(metric)}</p>
          </div>
        );
      })}
    </div>
  );
}

export default function ClientCampaignDetail() {
  const { campaignId } = useParams();
  const [report, setReport] = useState(null);
  const [media, setMedia] = useState([]);
  const [platform, setPlatform] = useState("");
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get(`/reports/campaign/${campaignId}`, {
        params: platform ? { platform } : {},
      });
      setReport(data);
      setError("");
    } catch (e) {
      setError(apiError(e));
    } finally {
      setRefreshing(false);
    }
  }, [campaignId, platform]);

  useEffect(() => {
    api
      .get("/media", { params: { campaign_id: campaignId } })
      .then((r) => setMedia(r.data.items || []))
      .catch(() => setMedia([]));
  }, [campaignId]);

  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  if (!report && error)
    return (
      <div data-testid="client-campaign-error">
        <ErrorNote message={error} onRetry={load} />
        <Link to="/client" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#F2811D]">
          <ArrowLeft className="h-4 w-4" /> Back to my campaigns
        </Link>
      </div>
    );
  if (!report) return <Loading label="Loading campaign dashboard…" />;

  const { campaign, totals, posting, insight_coverage: coverage } = report;

  return (
    <div data-testid="client-campaign-detail">
      <Link to="/client" className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-[#F2811D]">
        <ArrowLeft className="h-4 w-4" /> My campaigns
      </Link>

      <PageHeader
        title={campaign.name}
        description={`${report.client_org_name} · ${formatDate(campaign.start_date)} – ${formatDate(campaign.end_date)}`}
        onRefresh={load}
        refreshing={refreshing}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <ReportDownload campaignId={campaignId} platform={platform} testId="client-report-download" />
            <Badge testId="client-campaign-status">{campaign.status}</Badge>
          </div>
        }
      />
      <ErrorNote message={error} onRetry={load} />

      <div className="space-y-5">
        <Note tone="neutral" testId="client-metrics-disclaimer">
          Performance metrics are manually submitted by creators or the AnyTribe team. Updates
          appear after they are saved.
          {report.last_updated ? ` Last updated ${formatDateTime(report.last_updated)}.` : ""}
          {" "}
          {PLATFORM_METRIC_NOTE}
        </Note>

        {campaign.brief && (
          <Card testId="client-brief-card">
            <h2 className="text-xs font-bold uppercase tracking-[0.12em] text-[#8a8378]">Brief</h2>
            <p className="mt-2.5 whitespace-pre-line text-sm leading-relaxed body-text">
              {campaign.brief}
            </p>
          </Card>
        )}

        {media.length > 0 && (
          <Card testId="client-media-card">
            <h2 className="text-xs font-bold uppercase tracking-[0.12em] text-[#8a8378]">
              Shared files
            </h2>
            <ul className="mt-3 space-y-2.5">
              {media.map((file) => (
                <li key={file.id} className="flex flex-wrap items-center gap-2 text-sm">
                  <a
                    href={fileUrl(file.id)}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid={`client-media-${file.id}`}
                    className="inline-flex items-center gap-2 font-semibold text-[#F2811D] hover:underline"
                  >
                    <Download className="h-4 w-4" />
                    {file.label || file.original_filename}
                  </a>
                  <span className="text-xs body-text">{formatBytes(file.size)}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <Card testId="client-posting-progress">
            <h2 className="text-sm font-bold uppercase tracking-[0.1em] text-[#8a8378]">
              Posting progress
            </h2>
            <p className="mt-2 text-2xl font-extrabold tabular">
              {posting.expected === 0 ? "Not started" : `${posting.published} / ${posting.expected}`}
            </p>
            <p className="mt-1 text-sm body-text">published posts vs expected posts</p>
            <div className="mt-3">
              <ProgressBar percent={posting.percent ?? 0} />
            </div>
            {posting.expected > 0 && posting.published > posting.expected && (
              <p className="mt-2 text-xs font-semibold text-[#8A5A00]">
                More posts were published than originally expected.
              </p>
            )}
          </Card>

          <Card testId="client-insight-coverage">
            <h2 className="text-sm font-bold uppercase tracking-[0.1em] text-[#8a8378]">
              Insight reporting coverage
            </h2>
            <p className="mt-2 text-2xl font-extrabold tabular">
              {coverage.published === 0
                ? "Not started"
                : `${coverage.with_insights} / ${coverage.published}`}
            </p>
            <p className="mt-1 text-sm body-text">published posts with reported insights</p>
            <div className="mt-3">
              <ProgressBar percent={coverage.percent ?? 0} />
            </div>
            {report.pending_updates > 0 && (
              <p className="mt-2 text-xs font-semibold text-[#8A5A00]" data-testid="client-pending-updates">
                {report.pending_updates} post{report.pending_updates === 1 ? "" : "s"} showing
                "Update pending" — excluded from the totals until updated.
              </p>
            )}
          </Card>
        </div>

        <Card testId="client-performance-summary">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-extrabold">Performance summary</h2>
            {report.platforms.length > 1 && (
              <select
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                data-testid="client-platform-filter"
                className="rounded-full border border-[#E6E1D8] bg-white px-4 py-2 text-sm font-semibold"
              >
                <option value="">All platforms</option>
                {report.platforms.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="mt-5">
            <MetricTotals totals={totals} testIdPrefix="client-total" />
          </div>
          <p className="mt-4 text-xs body-text">
            Sum of reported reach — audiences may overlap. Only the latest eligible insight
            snapshot per post is counted; missing values are never treated as zero.
          </p>
        </Card>

        <Card testId="client-creator-roster">
          <h2 className="text-lg font-extrabold">Creator roster</h2>
          <div className="scroll-x mt-4">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="border-b border-[#EFEAE1] text-left text-xs font-bold uppercase tracking-[0.08em] text-[#8a8378]">
                  <th className="pb-2.5 pr-4">Creator</th>
                  <th className="pb-2.5 pr-4">Published / expected</th>
                  <th className="pb-2.5 pr-4">Insights reported</th>
                  {METRIC_KEYS.map((key) => (
                    <th key={key} className="pb-2.5 pr-4 text-right">
                      {METRIC_LABELS[key]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.creators.map((creator) => (
                  <tr
                    key={creator.creator_id}
                    className="border-b border-[#F5F1EA]"
                    data-testid={`client-creator-row-${creator.creator_id}`}
                  >
                    <td className="py-3 pr-4 font-semibold">{creator.creator_name}</td>
                    <td className="py-3 pr-4 tabular">
                      {creator.published_posts} / {creator.expected_posts || "—"}
                    </td>
                    <td className="py-3 pr-4 tabular">
                      {creator.published_posts === 0
                        ? "Not started"
                        : `${creator.posts_with_insights} / ${creator.published_posts}`}
                    </td>
                    {METRIC_KEYS.map((key) => (
                      <td key={key} className="py-3 pr-4 text-right tabular">
                        {creator.metrics[key].value == null
                          ? "—"
                          : formatNumber(creator.metrics[key].value)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card testId="client-post-breakdown">
          <h2 className="text-lg font-extrabold">Published content</h2>
          {report.posts.length === 0 ? (
            <div className="mt-4">
              <Empty
                testId="client-no-posts"
                title="No published posts yet"
                body="Posting links appear here as creators publish and submit them."
              />
            </div>
          ) : (
            <div className="scroll-x mt-4">
              <table className="w-full min-w-[980px] text-sm">
                <thead>
                  <tr className="border-b border-[#EFEAE1] text-left text-xs font-bold uppercase tracking-[0.08em] text-[#8a8378]">
                    <th className="pb-2.5 pr-4">Creator</th>
                    <th className="pb-2.5 pr-4">Platform</th>
                    <th className="pb-2.5 pr-4">Post</th>
                    <th className="pb-2.5 pr-4">Posted</th>
                    {METRIC_KEYS.map((key) => (
                      <th key={key} className="pb-2.5 pr-4 text-right">
                        {METRIC_LABELS[key]}
                      </th>
                    ))}
                    <th className="pb-2.5 pr-4">Metrics captured</th>
                    <th className="pb-2.5 pr-4">Source</th>
                    <th className="pb-2.5">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {report.posts.map((post) => (
                    <tr
                      key={post.post_id}
                      className="border-b border-[#F5F1EA]"
                      data-testid={`client-post-row-${post.post_id}`}
                    >
                      <td className="py-3 pr-4 font-semibold">{post.creator_name}</td>
                      <td className="py-3 pr-4">{post.platform}</td>
                      <td className="py-3 pr-4">
                        <a
                          href={post.url}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="inline-flex items-center gap-1.5 font-semibold text-[#F2811D] hover:underline"
                        >
                          Open <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </td>
                      <td className="py-3 pr-4">{formatDate(post.posting_date)}</td>
                      {METRIC_KEYS.map((key) => (
                        <td key={key} className="py-3 pr-4 text-right tabular">
                          {post.metrics[key] == null ? "—" : formatNumber(post.metrics[key])}
                        </td>
                      ))}
                      <td className="py-3 pr-4">{post.capture_date ? formatDate(post.capture_date) : "—"}</td>
                      <td className="py-3 pr-4 text-xs body-text">{post.source || "—"}</td>
                      <td className="py-3">
                        <Badge>{post.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
